import { OKENDO_STORE_URL } from '~/lib/constants';
import { generateStarRating } from '~/lib/helpers';
import { defineElement } from '~/utils';

const attributes = {
  productId: 'data-product-id',
  siblingProducts: 'data-sibling-products',
  reviewContainer: 'data-review-container',
};

class ProductReviewStars extends HTMLElement {
  constructor() {
    super();

    this.productId = this.getAttribute(attributes.productId);
    this.siblingProducts = this.getAttribute(attributes.siblingProducts) || '';
    if (this.siblingProducts) {
      this.siblingProducts = this.siblingProducts.split(',');
    }
    this.reviewContainer = this.querySelector(`[${attributes.reviewContainer}]`);
    if (!this.productId && this.siblingProducts.length === 0) {
      console.error('ProductReview component requires a product ID or sibling products.');
      return;
    }
    if (!this.reviewContainer) {
      console.error('ProductReview component requires a review container element.');
      return;
    }
  }

  connectedCallback() {
    if (this.reviewContainer) {
      this.renderReview();
    }
  }

  /**
   * Fetch product review data from the API.
   * @param {string} productId
   * @returns {Promise<Object|null>}
   */
  async fetchProductReview(productId) {
    try {
      const encodedProductId = encodeURIComponent(productId);
      const url = `${OKENDO_STORE_URL}/products/shopify-${encodedProductId}/review_aggregate`;
      const response = await fetch(url);
      if (!response.ok) {
        console.error(`Failed to fetch reviews for product ${productId}. HTTP status: ${response.status}`);
        return null;
      }
      const data = await response.json();

      sessionStorage.setItem(`product-${productId}-reviews`, JSON.stringify(data?.reviewAggregate));
      return data?.reviewAggregate || null;
    } catch (error) {
      console.error(`Error fetching reviews for product ${productId}:`, error);
      return null;
    }
  }

  /**
   * Fetch reviews for sibling products.
   * @returns {Promise<Object[]>}
   */
  async fetchSiblingProductsReviews() {
    const promises = this.siblingProducts.map((productId) => this.fetchProductReview(productId));
    const results = await Promise.all(promises);
    return results.filter((review) => review !== null);
  }

  /**
   * Render the review component.
   */
  async renderReview() {
    try {
      let reviews = [];

      if (this.siblingProducts.length > 0) {
        reviews = await this.fetchSiblingProductsReviews();
      } else if (this.productId) {
        const sessionData = sessionStorage.getItem(`product-${this.productId}-reviews`);
        if (sessionData) {
          reviews.push(JSON.parse(sessionData));
        } else {
          const review = await this.fetchProductReview(this.productId);
          if (review) reviews.push(review);
        }
      }

      if (reviews.length === 0) {
        this._displayNoReviews();
        return;
      }

      const { totalReviews, totalRatingValues } = reviews.reduce(
        (accumulator, review) => {
          accumulator.totalReviews += review.reviewCount || 0;
          accumulator.totalRatingValues += review.reviewRatingValuesTotal || 0;
          return accumulator;
        },
        { totalReviews: 0, totalRatingValues: 0 }
      );

      if (totalReviews === 0) {
        this._displayNoReviews();
        return;
      }

      const averageRating = totalRatingValues / totalReviews;
      const starsToDisplay = Math.round(averageRating);

      this._updateReviewContainer(totalReviews, starsToDisplay);
    } catch (error) {
      console.error('Error rendering reviews:', error);
    }
  }

  /**
   * Hide the component when there are no reviews.
   */
  _displayNoReviews() {
    this.reviewContainer.innerHTML = '';
  }

  /**
   * Update the review container with the fetched data.
   * @param {number} totalReviews
   * @param {number} starsToDisplay
   */
  _updateReviewContainer(totalReviews, starsToDisplay) {
    this.reviewContainer.innerHTML = '';

    // Create review rating element
    const reviewRatingDiv = document.createElement('div');
    reviewRatingDiv.classList.add('review-rating');
    if (this.siblingProducts.length > 0) {
      reviewRatingDiv.setAttribute('data-sibling-products', this.siblingProducts.join('-'));
    }

    // Generate star rating
    reviewRatingDiv.innerHTML = generateStarRating(starsToDisplay);

    // Create review count element
    const reviewCountDiv = document.createElement('div');
    reviewCountDiv.classList.add('review-count');
    reviewCountDiv.textContent = `${totalReviews.toLocaleString()} review${totalReviews !== 1 ? 's' : ''}`;

    // Append elements to review container
    this.reviewContainer.appendChild(reviewRatingDiv);
    this.reviewContainer.appendChild(reviewCountDiv);
  }
}

defineElement('product-review-stars', ProductReviewStars);
